import React, { useEffect, useState, useRef, useContext, useCallback } from 'react';
import { Row, Col, Dropdown } from 'react-bootstrap';
import styled from 'styled-components';
import Select, { components } from 'react-select';
import { FaChevronDown, FaEllipsisH } from 'react-icons/fa';
import { usePeer } from 'hooks/usePeer';
import ReactToPrint from 'react-to-print';
import { PeerReport, PeerOrganization, InputOption, InfoTooltipLink } from 'components';
import { reactLocalStorage } from 'reactjs-localstorage';
import _, { kebabCase, snakeCase, uniqBy } from 'lodash';
import {
  criteriaOptions,
  criteriaOptionsLeft as options,
  elegiOptions,
} from 'services/options-lists';
import { FaPrint } from 'react-icons/fa';
import Loader from 'react-loader-spinner';
import theme from '../../themes/default';
import { I18nContext } from 'i18n'
import { useProgramContext } from 'providers/programProvider';
import { useExportReports } from 'hooks/useExportReports'
import addAfter from 'utils/addAfter'
import uuid from 'uuid/v4';

const MENU_OPTIONS = [
  'download',
  'showAll',
  'hideAll',
  'reset'
]

const PeerComparisonReport = ({criteria, title}) => {
  const { translate } = useContext(I18nContext);
  const [suggestions, setSuggestions] = useState();
  const [criterias, setCriterias] = useState();
  const [peerOptions, setPeerOptions] = useState();
  const printRef = useRef(null);
  const showRef = useRef(null)
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(options[0]);
  const [state, dispatch] = useProgramContext()
  const [peers, setPeers] = useState();
  const [exportData, setExportData] = useState([])
  const { exportToSpreadsheet } = useExportReports(peers, criterias)

  const {
    dataOrganizations,
    loadingOrganizations,
    errorOrganizations,
    executeOrganizations,
    dataSingleOrg,
    loadingSingleOrg,
    errorSingleOrg,
    setId,
    data,
    loading,
    error,
    getReport,
  } = usePeer();

  const user = reactLocalStorage.getObject('user') || {};
  const {
    organization: { id },
  } = user;

  useEffect(() => {
    const { reportType, criterias, peers: defaultPeers, suggestions: defaultSuggestions } = state?.report?.[criteria] || {}
    const selected = reportType || options[0]
    if(reportType && defaultPeers) {
      setPeers(defaultPeers);
      setSuggestions(defaultSuggestions);
    } else {
      executeReport(selected)
    }
    setSelectedType(selected)
    setTimeout(() => {
      setReport(selected, 'reportType')
    }, 1000);
    if (criteria === 'peer') {
      setCriterias(criterias || criteriaOptions.slice(0, 12));
      setPeerOptions(criteriaOptions);
    } else {
      setCriterias(criterias || elegiOptions.slice(0, 6));
      setPeerOptions(elegiOptions);
    }
    executeOrganizations();
  }, [criteria]);

  useEffect(() => {
    if (data) {
      addCriteria()
      sortPeers(data);
    }
  }, [data])

  const addCriteria = useCallback(() => {
    let criteriaValue
    switch (selectedType.value) {
      case 'numberOfPrograms':
      case 'memberNumberOfPrograms':
      case 'proximityNumberOfPrograms':
        criteriaValue = 'numPrograms'
        break
      case 'annualAwardAmount':
      case 'memberAnnualAwardAmount':
      case 'proximityAnnualAward':
        criteriaValue = 'totalAwardAmount'
        break
      default:
        criteriaValue = null
        break
    }

    if (criteriaValue) {
      const newCriterias = uniqBy(
        addAfter(criterias, 2, criteriaOptions.find(({ value }) => value === criteriaValue)),
        'value'
      )
      setCriterias(newCriterias)
    }
  }, [selectedType, criterias])

  useEffect(() => {
    if(dataSingleOrg) {
      const [peer] = dataSingleOrg.peers;
      // take everything but the from peer except nspaMemberData and peerComparisonFactor
      const { nspaMemberData, peerComparisonFactor, ...rest } = peer ?? {};
      const newPeers = {  ...nspaMemberData, organization: { ...rest, peerComparisonFactor}, id: uuid() }
      
      setPeers([...peers, newPeers]);
    }
  }, [dataSingleOrg]);

  useEffect(() => {
    if (peers && suggestions) {
      const payload = {};
      payload[criteria] = { ...state.report?.[criteria] };
      payload[criteria]['peers'] = peers;
      payload[criteria]['suggestions'] = suggestions;
      dispatch({ type: `SET_REPORTS`, payload, criteria });
    }
  }, [peers, suggestions])

  const handleChange = (options) => {
    setCriterias(options)
    setReport(options, 'criterias')
  };

  const sortPeers = ({ peers }) => {
    //Send my org at the beggining
    const peer = uniqBy(peers.sort(function (x, y) {
      return x.organization?.id == id ? -1 : y.organization?.id == id ? 1 : 0;
    }), 'id')

    setSuggestions(peer.slice(4));
    setPeers(peer.slice(0, 4));
  };

  const setReport = (value, name) => {
    const payload = {};
    payload[criteria] = { ...state.report?.[criteria] };
    payload[criteria][name] = value;
    dispatch({ type: `SET_REPORTS`, payload, criteria });
  }

  const executeReport = (item) => {
    getReport(kebabCase(item?.value))
  }

  const handleTypeChange = async (item) => {
    //reset the data
    setSelectedType(item)
    executeReport(item);
    setReport(item, 'reportType')
  };

  const handleRemove = ({ id }) => {
    const newPeers = peers.filter((peer) => peer.id !== id);
    setPeers(newPeers);
  };

  const handleSuggestion = (item) => {
    setPeers([...peers, item]);

    //remove the added org from suggestions
    const newSuggestions = suggestions.filter(({ id }) => id !== item.id);
    setSuggestions(newSuggestions)
  };

  const handleOrganizationChange = (value) => {
    setId(value);
  };

  const renderNewColumns = () => {
    const col = [];
    const theTimes = 4 - peers?.length;
    _.times(theTimes, (index) => {
      col.push(
        <Col md="2" key={index}>
          {dataOrganizations && (
            <PeerOrganization
              suggestions={suggestions.slice(0, 10)}
              onSuggest={handleSuggestion}
              organizations={dataOrganizations?.peers}
              onChange={handleOrganizationChange}
            />
          )}
        </Col>
      );
    });
    return col;
  };

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <ChevronDown />
      </components.DropdownIndicator>
    );
  };

  const handleShow = () => {
    setMenuIsOpen(!menuIsOpen)
  }

  const DropdownIndicatorButton = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <Button onClick={handleShow}>
          {menuIsOpen ? 'Done' : 'Add'}
        </Button>
      </components.DropdownIndicator>
    );
  };

  const handleMenuOpen = () => {
    //Separate the options by statuses and order by label
    const selected = _.orderBy(criterias, 'label')
    const unselected =  _(criterias).xorWith(peerOptions).orderBy('label').value()

    //Merge into a single array, having first the selected ones and then the unselected
    //Both arrays ordered by label
    setPeerOptions([...selected, ...unselected])
  }

  const handleMenuClick = (option) => {
    switch (option) {
      case 'download':
        exportToSpreadsheet()
        break;
      case 'showAll':
        handleChange(peerOptions)
        break;
      case 'hideAll':
        handleChange([])
        break;
      default://reset
        const values = criteria === 'peer' ? criteriaOptions.slice(0, 12) : elegiOptions.slice(0, 6)
        const item =  options[0]
        setCriterias(values)
        setSelectedType(item)
        const payload = {}
        payload[criteria] = { ...state.report?.[criteria] }
        payload[criteria]['reportType'] = item
        payload[criteria]['criterias'] = values
        dispatch({ type: `SET_REPORTS`, payload, criteria })
        executeReport(item)
        break;
    }
  }

  return (
    <>
      <Row>
        <Col md={2}>{translate('report_organizations')}:</Col>
        <Col md={4} style={{ zIndex: 1022, paddingRight: 0 }}>
          <StyledSelect
            options={options}
            value={selectedType}
            components={{ DropdownIndicator }}
            onChange={handleTypeChange}
          />
        </Col>
        <Col md="5" className="text-right">
          <StyledDropDown>
            <Dropdown.Toggle variant="secondary" id="dropdown-basic">
              <FaEllipsisH />
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {MENU_OPTIONS.map((option) => (
                <Dropdown.Item
                  as="button"
                  key={`report_${option}`}
                  onClick={() => handleMenuClick(option)}
                >
                  {translate(snakeCase(`report_${option}`))}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </StyledDropDown>
        </Col>
        <Col md="1" className='text-right'>
          <ReactToPrint
            trigger={() => (
              <PrintButton>
                <FaPrint /> Print
              </PrintButton>
            )}
            content={() => printRef.current}
          />
        </Col>
      </Row>
      <Row>
        <Col md={2}>Show Criteria:</Col>
        <Col style={{ zIndex: 1021 }}>
          <StyledSelect
            options={peerOptions}
            value={criterias}
            onChange={handleChange}
            isMulti
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{
              Option: InputOption,
              DropdownIndicator: DropdownIndicatorButton
            }}
            ref={showRef}
            onFocus={() => setMenuIsOpen(true)}
            onBlur={() => setMenuIsOpen(false)}
            isClearable={false}
            onMenuOpen={handleMenuOpen}
          />
        </Col>
      </Row>
      {loading ? (
        <Spinner>
          <Loader
            type="Oval"
            height={30}
            color={theme.palette.primary[0]}
            timeout={10000}
          />
        </Spinner>
      ) : (
        <ReportRow ref={printRef}>
          <Col>
            <PeerReport
              peers={peers}
              criterias={criterias}
              onRemove={handleRemove}
              loading={loading}
              title={title}
              reportType={selectedType?.label}
            />
          </Col>
          {renderNewColumns()}
        </ReportRow>
      )}
      <Row>
        <Col className="d-flex">
          <DashesMean data-end="?">{translate('dashes_mean')}</DashesMean>
          <InfoTooltipLink 
            text={translate('dashes_mean_text')}
            placement="top"
          />
        </Col>
      </Row>
    </>
  );
};

const ReportRow = styled(Row)`
  @page {
    margin: 1rem 2rem;
  }
`

const DashesMean = styled.span`
  margin-right: .7rem;
  &::after {
    content: attr(data-end);
    font-size: .9rem;
  }
`

const ChevronDown = styled(FaChevronDown)`
  color: ${({ theme }) => theme.palette.grayscale[2]};
  font-size: 1.2rem;
`;

const StyledSelect = styled(Select)`
  margin-bottom: 2rem;

  > div {
    border-color: ${({ theme }) => theme.palette.grayscale[6]};
    border-radius: 0;
    background-color: ${({ theme }) => theme.palette.grayscale[8]};

    [class*='ValueContainer'] {
      position: unset;
      > div {
        color: ${({ theme }) => theme.palette.grayscale[3]};
      }
    }

    [class*='IndicatorsContainer'] {
      height: 2rem;
      > span {
        background-color: ${({ theme }) => theme.palette.grayscale[8]};
      }
    }
  }
`;

const PrintButton = styled.button`
  border: none;
  background-color: ${({ theme }) => theme.palette.grayscale[8]};
  color: ${({ theme }) => theme.palette.primary[0]};
`;

const Spinner = styled.div`
  text-align: center;
`;

const Button = styled.button`
  background-color: ${({ theme }) => theme.palette.primary[0]};
  border: none;
  color: ${({ theme }) => theme.palette.grayscale[8]};
  padding: 0.5rem 1rem;
  margin-top: 0.3rem;
  font-size: 0.8rem;
  height: 2rem;
`

const StyledDropDown = styled(Dropdown)`
&.show > .btn-secondary.dropdown-toggle {
  background-color: ${({ theme }) => theme.palette.primary[0]};
  box-shadow: none;

  > svg {
    color: ${({ theme }) => theme.palette.grayscale[8]};
  }
}
> button {
  background-color: ${({ theme }) => theme.palette.grayscale[6]};
  border: 0;

  > svg {
    color: ${({ theme }) => theme.palette.grayscale[1]};
    font-size: 1.1rem;
  }

  &::after {
    display: none;
  }

  &:active, &:hover {
    background-color:  ${({ theme }) => theme.palette.primary[0]} !important;
    border: 0;
    box-shadow: none !important;

    > svg {
      color: ${({ theme }) => theme.palette.grayscale[8]};
    }
  }

  &:focus {
    background-color: ${({ theme }) => theme.palette.grayscale[6]};
    box-shadow: none;
  }
}
  .dropdown-menu {
    z-index: 1023;
  }
`

export default PeerComparisonReport;
